/**
 * Continuity Assets — type registry.
 *
 * Adding a new asset type later (e.g. real-estate deeds, art, safety-deposit
 * boxes) is done here + a form component under ./forms — no rebuild.
 *
 * Each type declares:
 *   key         — matches the backend ALLOWED_TYPES enum
 *   label       — user-facing plural label ("Automobiles")
 *   singular    — user-facing singular ("an Automobile")
 *   icon        — lucide-react component
 *   accent      — tailwind classes for the card/tab tint
 *   summarise   — a fn(details) → one-line preview string for the card
 *   keyFields   — curated fields that a Trusted Contact actually needs
 *                 in order to locate/manage the asset. Drives the amber
 *                 "Add {label}" nudge chips on ContinuityAssetCard.
 *
 *                 Each entry:
 *                   { key, label, where: 'details' | 'root', kind?: 'array' }
 *                 where === 'details' → field lives inside asset.details
 *                 where === 'root'    → field lives on asset (nickname, etc.)
 *                 kind  === 'array'   → treat empty array as missing
 */

import { Car, ShieldCheck, Plane, Ship } from 'lucide-react';

const num = (n) => (Number.isFinite(n) ? String(n) : '');

export const ASSET_TYPES = [
  {
    key: 'automobile',
    label: 'Automobiles',
    singular: 'an Automobile',
    icon: Car,
    accent: { badge: 'bg-teal-100 text-teal-800', ring: 'border-teal-200' },
    summarise: (d = {}) =>
      [d.year, d.make, d.model].filter(Boolean).join(' ') || 'Automobile',
    keyFields: [
      { key: 'make', label: 'Make', where: 'details' },
      { key: 'model', label: 'Model', where: 'details' },
      { key: 'year', label: 'Year', where: 'details' },
      { key: 'registration_number', label: 'Plate', where: 'details' },
      { key: 'ownership_type', label: 'Ownership', where: 'details' },
      { key: 'insurance_reference', label: 'Insurance', where: 'details' },
      { key: 'title_location', label: 'Where the title lives', where: 'details' },
      { key: 'estimated_value', label: 'Value', where: 'root' },
    ],
  },
  {
    key: 'life_insurance',
    label: 'Life Insurance',
    singular: 'a Life Insurance policy',
    icon: ShieldCheck,
    accent: { badge: 'bg-amber-100 text-amber-800', ring: 'border-amber-200' },
    summarise: (d = {}) =>
      [d.insurer_name, d.policy_type].filter(Boolean).join(' — ') || 'Life Insurance policy',
    keyFields: [
      { key: 'insurer_name', label: 'Insurer', where: 'details' },
      { key: 'policy_type', label: 'Policy type', where: 'details' },
      { key: 'policy_number', label: 'Policy number', where: 'details' },
      { key: 'premium_amount', label: 'Premium', where: 'details' },
      { key: 'beneficiaries', label: 'Beneficiaries', where: 'details', kind: 'array' },
    ],
  },
  {
    key: 'private_jet',
    label: 'Private Jets',
    singular: 'a Private Jet',
    icon: Plane,
    accent: { badge: 'bg-sky-100 text-sky-800', ring: 'border-sky-200' },
    summarise: (d = {}) =>
      [num(d.year), d.manufacturer, d.model, d.tail_number ? `(${d.tail_number})` : '']
        .filter(Boolean)
        .join(' ') || 'Private Jet',
    keyFields: [
      { key: 'manufacturer', label: 'Manufacturer', where: 'details' },
      { key: 'model', label: 'Model', where: 'details' },
      { key: 'year', label: 'Year', where: 'details' },
      { key: 'tail_number', label: 'Tail number', where: 'details' },
      { key: 'ownership_type', label: 'Ownership', where: 'details' },
      { key: 'hangar_location', label: 'Hangar', where: 'details' },
      { key: 'insurance_reference', label: 'Insurance', where: 'details' },
      { key: 'estimated_value', label: 'Value', where: 'root' },
    ],
  },
  {
    key: 'yacht',
    label: 'Yachts',
    singular: 'a Yacht',
    icon: Ship,
    accent: { badge: 'bg-indigo-100 text-indigo-800', ring: 'border-indigo-200' },
    summarise: (d = {}) =>
      [d.vessel_name && `"${d.vessel_name}"`, d.manufacturer, d.model]
        .filter(Boolean)
        .join(' ') || 'Yacht',
    keyFields: [
      { key: 'vessel_name', label: 'Vessel name', where: 'details' },
      { key: 'manufacturer', label: 'Builder', where: 'details' },
      { key: 'model', label: 'Model', where: 'details' },
      { key: 'registration_number', label: 'Registration', where: 'details' },
      { key: 'flag_country', label: 'Flag country', where: 'details' },
      { key: 'ownership_type', label: 'Ownership', where: 'details' },
      { key: 'marina_location', label: 'Marina', where: 'details' },
      { key: 'insurance_reference', label: 'Insurance', where: 'details' },
      { key: 'estimated_value', label: 'Value', where: 'root' },
    ],
  },
];

export const findAssetType = (key) => ASSET_TYPES.find((t) => t.key === key);

/**
 * Given an asset and its type meta, return the list of keyFields whose
 * value is still empty. Used by ContinuityAssetCard to render the amber
 * "Add {label}" nudge chips.
 */
export const missingKeyFields = (asset, meta) => {
  if (!asset || !meta || !meta.keyFields) return [];
  const details = asset.details || {};
  return meta.keyFields.filter((f) => {
    const source = f.where === 'root' ? asset : details;
    const v = source[f.key];
    if (f.kind === 'array') return !Array.isArray(v) || v.length === 0;
    if (v === null || v === undefined) return true;
    if (typeof v === 'string' && v.trim() === '') return true;
    return false;
  });
};
